import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { getFieldsForCard } from 'core/components/InfoPanel'
import InfoCard from 'k8s/components/common/entity/info-card'
import Card from 'core/elements/card'
import Progress from 'core/components/progress/Progress'
import Grid from 'core/elements/grid'
import { routeManager } from 'core/utils/routes'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { listResmgrHosts, getHostNetworkConfigs } from 'openstack/components/infrastructure/actions'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { hasHypervisorRole } from 'openstack/components/infrastructure/helpers'
import SimpleLink from 'core/components/SimpleLink'
import { IHostDetailsPageTabs } from 'openstack/components/infrastructure/hosts/host-details/model'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '633px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
  },
}))

const metadataFields = [
  {
    id: 'name',
    title: 'Name',
  },
  {
    id: 'id',
    title: 'ID',
  },
  {
    id: 'router:external',
    title: 'External Network',
    render: (external) => (external ? 'True' : 'False'),
  },
  {
    id: 'provider:network_type',
    title: 'Network Type',
  },
  {
    id: 'provider:physical_network',
    title: 'Physical Network',
  },
  {
    id: 'provider:segmentation_id',
    title: 'Segmentation ID',
  },
  {
    id: 'port_security_enabled',
    title: 'Port Security',
    render: (enabled) => (enabled ? 'Enabled' : 'Disabled'),
  },
  {
    id: 'status',
    title: 'Status',
  },
  {
    id: 'admin_state_up',
    title: 'Admin State',
    render: (isUp) => (isUp ? 'Up' : 'Down'),
  },
  {
    id: 'dns_domain',
    title: 'DNS Domain',
  },
  {
    id: 'shared',
    title: 'Shared',
    render: (shared) => (shared ? 'True' : 'False'),
  },
]

const hypervisorTableColumns = [
  {
    key: 'name',
    label: 'Name',
    render: (val, host) => {
      return (
        <SimpleLink
          src={routeManager.routes.openstack.hostDetails.path({
            id: host?.id,
            tab: IHostDetailsPageTabs.Overview,
          })}
        >
          {host?.name || host?.id}
        </SimpleLink>
      )
    },
  },
  { key: 'mappedInterface', label: 'Interface' },
]

export default function Overview({ network, loading }) {
  const classes = useStyles({})

  const metadata = useMemo(() => {
    return getFieldsForCard(metadataFields, network)
  }, [network])

  const [hypervisorInfo, setHypervisorInfo] = useState([])
  const { loading: loadingHosts } = useListAction(listResmgrHosts)
  const hostData = useSelector(resmgrHostsSelector)

  useEffect(() => {
    const getHostConfigs = async () => {
      const hostNetworkConfigurations: any = await getHostNetworkConfigs()
      const hostConfigsById = hostNetworkConfigurations.reduce((accum, config) => {
        return {
          ...accum,
          [config.id]: config,
        }
      }, {})
      const hypervisors = hostData.filter(hasHypervisorRole)
      const hypervisorsWithHostConfig = hypervisors.map((hypervisor) => {
        return {
          ...hypervisor,
          mappedInterface:
            hostConfigsById[hypervisor.hostconfig_id]?.networkLabels?.[
              network?.['provider:physical_network']
            ],
        }
      })
      setHypervisorInfo(hypervisorsWithHostConfig)
    }
    if (isAdmin() && network && hostData?.length) {
      getHostConfigs()
    }
  }, [network, hostData])

  return (
    <Progress loading={loading}>
      <div className={classes.overview}>
        <div className={classes.column}>
          <InfoCard items={metadata} title="Properties" />
        </div>
        <div className={classes.column}>
          <Card title="Associated Hypervisor Interfaces" withCustomBody>
            <Grid
              uniqueIdentifier="id"
              data={hypervisorInfo}
              columns={hypervisorTableColumns}
              loading={loadingHosts}
              compact
              disableToolbar
            />
          </Card>
        </div>
      </div>
    </Progress>
  )
}
